import React from 'react';
import { Lock, Zap, Coins } from 'lucide-react';
import { Button } from '@/Components/ui/button';

/**
 * Shared plan-gating UI for feature popups. One design for every gated action:
 *  - PlanLockCard          : feature not in the company's plan (shows required tier + current plan + upgrade CTA)
 *  - InsufficientCreditsCard: in plan, but balance too low (top-up CTA)
 *  - CreditCostHint         : allowed — shows what a proceed will deduct
 *
 * The required tier mirrors the plan_features matrix (Pilot 5 / Growth 11 /
 * Scale 16 / Enterprise 17). Pilot-tier features are never locked for a paid
 * plan, so they aren't listed here.
 */
export const FEATURE_REQUIRED_TIER = {
  realtime_interview: 'Growth',
  coding_test: 'Growth',
  live_meet: 'Growth',
  whatsapp: 'Growth',
  video_resume: 'Growth',
  offer_letter: 'Growth',
  ai_phone_screening: 'Scale',
  auto_screen_on_apply: 'Scale',
  market_trends: 'Scale',
  premium_college_tier: 'Scale',
  identity_verification: 'Scale',
  api_access: 'Enterprise',
};

const goToBilling = () => {
  try { window.location.assign('/hirewithus/billing'); } catch (_) { /* no-op */ }
};

const plural = (label) => (String(label).trim().toLowerCase().endsWith('s') ? 'are' : 'is');
const pluralNeg = (label) => (String(label).trim().toLowerCase().endsWith('s') ? "aren't" : "isn't");
const creditWord = (n) => (Number(n) === 1 ? 'credit' : 'credits');

/** Feature not included in the plan. */
export function PlanLockCard({ featureKey, featureLabel, planName, cost = null, className = '' }) {
  const tier = FEATURE_REQUIRED_TIER[featureKey] || 'a higher';
  const costText = cost != null ? ` (${cost} ${creditWord(cost)} each)` : '';
  return (
    <div className={`relative overflow-hidden rounded-xl border border-amber-300/70 bg-gradient-to-br from-amber-50 via-white to-white p-4 shadow-sm ${className}`}>
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 ring-4 ring-amber-50">
          <Lock className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-slate-900">
              {featureLabel} {plural(featureLabel)} a premium feature
            </p>
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
              {tier} +
            </span>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-slate-600">
            {featureLabel} {pluralNeg(featureLabel)} included in your{' '}
            <strong className="text-slate-800">{planName || 'current'}</strong> plan. Upgrade to{' '}
            <strong className="text-slate-800">{tier}</strong> or higher to use {String(featureLabel).toLowerCase()}{costText}.
          </p>
          <Button size="sm" className="mt-3 h-8 gap-1.5 px-3 text-xs font-semibold" onClick={goToBilling}>
            <Zap className="h-3.5 w-3.5" />
            Upgrade plan
          </Button>
        </div>
      </div>
    </div>
  );
}

/** In plan, but not enough credits. */
export function InsufficientCreditsCard({ featureLabel, cost, balance, className = '' }) {
  return (
    <div className={`flex items-start gap-3 rounded-xl border border-amber-300/70 bg-amber-50 p-4 shadow-sm ${className}`}>
      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 ring-4 ring-amber-50">
        <Coins className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <p className="text-sm font-semibold text-slate-900">Not enough credits</p>
        <p className="mt-1 text-xs leading-relaxed text-slate-600">
          {featureLabel} costs <strong className="text-slate-800">{cost} {creditWord(cost)}</strong>{' '}
          and you have <strong className="text-slate-800">{balance}</strong>. Please top up to continue.
        </p>
        <Button size="sm" variant="outline" className="mt-3 h-8 gap-1.5 px-3 text-xs font-semibold" onClick={goToBilling}>
          <Coins className="h-3.5 w-3.5" />
          Buy credits
        </Button>
      </div>
    </div>
  );
}

/** Allowed — quiet cost hint. */
export function CreditCostHint({ label, cost, className = '' }) {
  const n = Number(cost);
  return (
    <div className={`flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs text-emerald-800 ${className}`}>
      <Coins className="h-3.5 w-3.5 flex-shrink-0" />
      <span>{label} · <strong>{n} {creditWord(n)}</strong> deducted when you proceed.</span>
    </div>
  );
}

export default PlanLockCard;
